import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, HostListener, ViewChild, computed, inject, linkedSignal, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { OpsNotice, OpsNoticeTone, OpsNoticesService } from '../../../core/services/ops-notices.service';

const TONE_ORDER: Record<OpsNoticeTone, number> = { danger: 0, warning: 1, info: 2 };
const TONE_ICON: Record<OpsNoticeTone, string> = {
  danger: 'fa-circle-exclamation text-maersk-error',
  warning: 'fa-triangle-exclamation text-maersk-warning',
  info: 'fa-circle-info text-maersk-text-muted',
};
const TONE_LABEL: Record<OpsNoticeTone, string> = { danger: 'Problem', warning: 'Warning', info: 'Info' };

let nextPanelId = 0;

/** Header bell: a disclosure listing the notices of OpsNoticesService. */
@Component({
  selector: 'app-ops-notices',
  standalone: true,
  imports: [RouterLink],
  host: { class: 'flex lg:relative' },
  template: `
    <button
      #trigger
      type="button"
      class="ops-btn ops-btn-secondary ops-btn--icon relative"
      title="Notifications"
      [attr.aria-label]="label()"
      [attr.aria-expanded]="open()"
      [attr.aria-controls]="panelId"
      (click)="toggle()">
      <i class="fas fa-bell text-sm" aria-hidden="true"></i>
      @if (count() > 0) {
        <span
          class="ops-badge ops-badge--pill absolute -top-2 -right-2 min-w-5 justify-center"
          [class]="tone() === 'danger' ? 'ops-badge-danger' : 'ops-badge-warning'"
          aria-hidden="true">
          {{ count() }}
        </span>
      }
    </button>

    <!-- Outside the hidden panel, so a new problem is said while the panel is closed. A new node per rise is said again. -->
    <span class="sr-only" role="status" aria-live="polite" data-test="notices-announcement">
      @for (said of [announcement()]; track said.id) {
        <span>{{ said.text }}</span>
      }
    </span>

    <!-- Below lg the panel is placed under the header bar, so it stays on screen beside the user menu on phones. -->
    <div
      [id]="panelId"
      class="fixed right-4 top-15 z-50 w-[min(24rem,100vw_-_2rem)] overflow-hidden rounded-lg border border-maersk-border bg-white shadow-lg lg:absolute lg:right-0 lg:top-full lg:mt-1"
      [hidden]="!open()">
      <div class="flex items-baseline justify-between gap-3 border-b border-maersk-border px-4 py-2">
        <h2 class="text-sm font-semibold text-maersk-text">Notifications</h2>
        @if (checkedText()) {
          <span class="text-xs text-maersk-text-muted">{{ checkedText() }}</span>
        }
      </div>
      <div class="max-h-[calc(100vh_-_6rem)] overflow-y-auto">
        @if (!notices()) {
          <p class="px-4 py-3 text-sm text-maersk-text-secondary">Not checked yet</p>
        } @else {
          @if (count() === 0) {
            <p class="flex items-center gap-2 px-4 py-3 text-sm text-maersk-text-secondary">
              <i class="fas fa-circle-check text-maersk-green" aria-hidden="true"></i>
              No problems found
            </p>
          }
          @if (listed().length) {
            <ul class="divide-y divide-maersk-border">
              @for (notice of listed(); track notice.id) {
                <li class="flex items-start gap-2.5 px-4 py-2.5" [attr.data-tone]="notice.tone">
                  <i class="fas mt-0.5 w-4 shrink-0 text-center text-sm" [class]="toneIcon[notice.tone]" role="img" [attr.aria-label]="toneLabel[notice.tone]"></i>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm" [class]="notice.tone === 'info' ? 'text-maersk-text-secondary' : 'font-medium text-maersk-text'">{{ notice.title }}</p>
                    @if (notice.detail) {
                      <p class="mt-0.5 text-xs text-maersk-text-secondary">{{ notice.detail }}</p>
                    }
                    @if (notice.link; as link) {
                      <a class="mt-1 inline-block text-xs font-medium" [routerLink]="link.route" [queryParams]="link.queryParams" (click)="close()">
                        {{ link.label }}
                      </a>
                    }
                  </div>
                </li>
              }
            </ul>
          }
        }
      </div>
    </div>
  `,
})
export class OpsNoticesComponent {
  private readonly service = inject(OpsNoticesService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly document = inject(DOCUMENT);
  @ViewChild('trigger', { static: true }) private trigger!: ElementRef<HTMLButtonElement>;

  protected readonly panelId = `ops-notices-panel-${nextPanelId++}`;
  protected readonly toneIcon = TONE_ICON;
  protected readonly toneLabel = TONE_LABEL;
  protected readonly open = signal(false);
  protected readonly notices = this.service.notices;
  protected readonly count = this.service.count;
  protected readonly tone = this.service.tone;

  /** Danger, then warning, then the uncounted info lines. */
  protected readonly listed = computed<OpsNotice[]>(() =>
    [...(this.notices() ?? [])].sort((left, right) => TONE_ORDER[left.tone] - TONE_ORDER[right.tone])
  );

  /** e.g. 'Notifications, 1 problem and 2 warnings': the badge shows the severity by its colour only. */
  protected readonly label = computed(() => {
    const notices = this.notices() ?? [];
    const counted = (tone: OpsNoticeTone, noun: string) => {
      const count = notices.filter(notice => notice.tone === tone).length;
      return count ? `${count} ${noun}${count === 1 ? '' : 's'}` : '';
    };
    const summary = [counted('danger', 'problem'), counted('warning', 'warning')].filter(Boolean).join(' and ');
    return summary ? `Notifications, ${summary}` : 'Notifications';
  });

  /**
   * Said politely when more notices need attention than at the last check, not on every check. Each rise gets its own
   * id, so a count that falls and rises again to the same text is said again.
   */
  protected readonly announcement = linkedSignal<{ count: number; label: string }, { id: number; text: string }>({
    source: () => ({ count: this.count(), label: this.label() }),
    computation: (source, previous) => {
      if (!previous) return { id: 0, text: '' };
      return source.count > previous.source.count ? { id: previous.value.id + 1, text: source.label } : previous.value;
    },
  });

  protected readonly checkedText = computed(() => {
    const at = this.service.checkedAt();
    if (!at) return '';
    const pad = (value: number) => value.toString().padStart(2, '0');
    return `Checked ${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())} UTC`;
  });

  toggle(): void {
    this.open.update(open => !open);
  }

  close(): void {
    this.open.set(false);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.open()) return;
    const active = this.document.activeElement;
    const focusInside = !active || active === this.document.body || this.host.nativeElement.contains(active);
    this.close();
    // Focus moved elsewhere on purpose stays there.
    if (focusInside) this.trigger.nativeElement.focus();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) this.close();
  }
}
